/**
 * useAgentFiles Hook
 * Manage file attachments for agents
 */

import { useState, useCallback } from 'react';
import adminApi from '@/services/admin.api';
import { FileSourceItem } from './useFileSources';

interface UseAgentFilesResult {
  files: FileSourceItem[];
  loading: boolean;
  error: string;
  loadFiles: (agentId: string) => Promise<void>;
  detachFile: (agentId: string, fileId: string, deleteFile?: boolean) => Promise<void>;
}

// Helper function to extract error message from API response
const extractErrorMessage = (error: any): string => {
  if (error.response?.data?.error?.message) {
    return error.response.data.error.message;
  }
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

export const useAgentFiles = (): UseAgentFilesResult => {
  const [files, setFiles] = useState<FileSourceItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadFiles = useCallback(async (agentId: string) => {
    try {
      setLoading(true);
      setError('');
      const filesData = await adminApi.getAgentFiles(agentId);
      setFiles(filesData);
    } catch (err: any) {
      setError(extractErrorMessage(err));
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const detachFile = useCallback(
    async (agentId: string, fileId: string, deleteFile: boolean = false) => {
      try {
        setError('');
        await adminApi.detachFileFromAgent(agentId, fileId, deleteFile);
        // Remove from local state
        setFiles((prev) => prev.filter((f) => f._id !== fileId));
      } catch (err: any) {
        throw new Error(extractErrorMessage(err));
      }
    },
    []
  );

  return {
    files,
    loading,
    error,
    loadFiles,
    detachFile,
  };
};
